import { PickOne } from '@helpers/PickOne';
import { Device, until } from '@helpers/media';
import useOnClickOutside from '@hooks/useClickOutside';
import { useInView } from '@hooks/useInView';
import useMedia from '@hooks/useMedia';
import useProductCards from '@hooks/useProductCards';
import Chevron from '@img/icons/chevron.svg';
import Tick from '@img/icons/icon-tick.svg';
import Minus from '@img/icons/minus.svg';
import { CompareItem } from '@state/ducks/compare/compare.reducer';
import TextTag from '@stories/Elements/Content/TextTag/TextTag';
import Button, { ButtonProps } from '@stories/Elements/Forms/Buttons/Button';
import RatingStars, { getActiveStars } from '@stories/Elements/Misc/RatingStars/RatingStars';
import ImageTransform from '@tti/image-transform';
import React, { useState } from 'react';
import { ProductCardStyles as s } from './ProductCard.styles';

export interface ProductCardProps {
  product: ProductViewModel;
  buttonText: string;
  index?: number;
  /**
   * Used to set the delayInterval for animation based of how many items in a given row
   */
  rowItems?: number;
  pngImage?: boolean;
  background?: 'white' | 'none';
  animate?: boolean;
  pageContext?: PageContext;
  showPrice?: boolean;
  translations?: Record<string, string | null>;
  isLink?: boolean;
  disableLink?: boolean;
  target?: '_blank' | '_self';
  onClick?: () => void;
  showSystemLogos?: boolean;
  icons?: ProductViewModelIconMapping[] | null;
  showCode?: boolean;
  showCompare?: boolean;
  updateCompare?: (agilityId: number, image: string, productName: string, modelCode: string) => void;
  isInCompare?: boolean;
  compareItems?: CompareItem[];
  productInfoModal?: boolean;
  setShowInfoModal?: (product: ProductViewModel) => void;
  hidePublishedTag?: boolean;
}

const ProductCard: React.VFC<ProductCardProps> = ({
  product,
  buttonText,
  index = 0,
  rowItems = useMedia([until(Device.MobileLarge), until(Device.TabletLarge)], [1, 2], 3),
  pngImage = false,
  background = 'white',
  animate = true,
  showPrice = false,
  translations = {},
  isLink = true,
  disableLink = false,
  target = '_self',
  showSystemLogos = false,
  icons = [],
  showCode = true,
  onClick,
  showCompare,
  updateCompare,
  isInCompare,
  compareItems,
  setShowInfoModal,
  productInfoModal = false,
  hidePublishedTag = false,
}) => {
  const [elementRef, inView] = useInView<HTMLDivElement>({ threshold: 0.15 });
  const [dropdownActive, setDropdownActive] = useState<boolean>(false);
  const [activeVariantId, setActiveVariantId] = useState<number>(
    product.objectType === 'NetC.ProductModel' && product.productVariants && product.productVariants.length > 0
      ? product.productVariants[0].agilityId
      : product.agilityId,
  );
  const activeVariant = React.useMemo(() => product.productVariants?.find((x) => x.agilityId === activeVariantId), [
    activeVariantId,
  ]);

  const [variantImageUrl, setVariantImageUrl] = React.useState('');
  const productImageUrl = variantImageUrl ? variantImageUrl : pngImage ? product.pngListingImage : product.listingImage;

  const state = useProductCards();
  const rowMapping = state?.mappings.get(!(rowItems === 0 && index === 0) ? Math.floor(index / rowItems) : 0);

  const baseDelay = 0;
  const indexDelay = index % rowItems;
  const delayInterval = baseDelay + indexDelay * 100;

  useOnClickOutside(elementRef, () => {
    if (!dropdownActive) {
      return;
    }

    setDropdownActive(false);
  });

  const renderIcons = (): React.ReactNode => {
    if (icons === null || icons.length === 0) {
      return null;
    }

    const iconsToRender = [];

    for (const mapping of icons) {
      const productIcon = product.icons?.find((x) => x.iconGuid === mapping.iconMappingGuid);

      if (!productIcon) {
        continue;
      }

      iconsToRender.push(
        <>
          {productIcon.attribute1Value && (
            <s.IconWrapper key={`${mapping.iconMappingGuid}-1`}>
              <s.IconInner dangerouslySetInnerHTML={{ __html: mapping.icon1Markup ?? '' }}></s.IconInner>
              <span>{productIcon.attribute1Value}</span>
            </s.IconWrapper>
          )}
          {productIcon.attribute2Value && (
            <s.IconWrapper key={`${mapping.iconMappingGuid}-2`}>
              <s.IconInner dangerouslySetInnerHTML={{ __html: mapping.icon2Markup ?? '' }}></s.IconInner>
              <span>{productIcon.attribute2Value}</span>
            </s.IconWrapper>
          )}
        </>,
      );
    }

    if (iconsToRender.length === 0) {
      if (rowMapping?.hasAttributeIcons) {
        return <s.Icons></s.Icons>;
      } else {
        return null;
      }
    }

    return <s.Icons>{iconsToRender}</s.Icons>;
  };

  const getButtonTheme = (): PickOne<ButtonProps, 'theme'> => {
    if (showPrice) {
      return 'red';
    }

    if (background === 'none') {
      return 'white';
    }

    return 'black-grey';
  };

  const displayAsLink =
    (product.isPublished || (!product.isPublished && !productInfoModal)) && isLink && product.url.length > 0;

  return (
    <>
      <s.Card
        data-product-agility-id={product.agilityId}
        data-has-stars={getActiveStars(product.averageReviewRating) > 0 || rowMapping?.hasReviews}
        data-has-new={rowMapping?.isNew}
        ref={elementRef}
        draggable={false}
      >
        <s.CardContent
          inView={animate ? inView : true}
          delay={delayInterval}
          background={background}
          isLink={displayAsLink}
          href={displayAsLink ? (activeVariant ? activeVariant.url : product.url) : 'javascript:void(0)'}
          onClick={(e) => {
            if (onClick) {
              e.preventDefault();
              e.stopPropagation();
              onClick();
            }

            if (displayAsLink && !disableLink) {
              if (activeVariant) {
                window.location.href = activeVariant.url;
              } else {
                window.location.href = product.url;
              }
            }
          }}
          showBorder={!!product.tagText}
          borderPadding={!!product.tagText || rowMapping?.isNew === true}
          target={target}
          draggable={false}
        >
          {!!product.tagText && (
            <s.TagWrapper>
              <TextTag text={product.tagText} theme="red" />
            </s.TagWrapper>
          )}

          {!product.isPublished && hidePublishedTag !== true ? (
            <s.Unpublished>UNPUBLISHED</s.Unpublished>
          ) : (
            <>{rowMapping?.hasUnpublishedTag && hidePublishedTag !== true ? <s.Unpublished></s.Unpublished> : null}</>
          )}

          {showCode ? (
            <s.CodeWrapper>
              <s.Code data-cta-text data-product-model-code={product.modelCode}>
                {product.modelCode}
              </s.Code>
            </s.CodeWrapper>
          ) : null}

          {showSystemLogos && product.systemLogo && product.systemLogo.whiteBg ? (
            <s.LogoWrapper>
              <img
                src={product.systemLogo.whiteBg}
                alt={product.systemLogo.description ? product.systemLogo.description : undefined}
              />
            </s.LogoWrapper>
          ) : (
            <>{rowMapping?.hasSystemIcon && showSystemLogos ? <s.LogoWrapper></s.LogoWrapper> : null}</>
          )}

          <s.MediaWrapper>
            <img
              alt={product.title}
              src={`${new ImageTransform(productImageUrl).width(337)}`}
              loading="lazy"
              draggable={false}
              data-product-image={new ImageTransform(productImageUrl).width(337)}
            />
          </s.MediaWrapper>

          {renderIcons()}

          {product.productVariants && (
            <s.VariantSelector>
              {product.productVariants?.length > 1 ? (
                <>
                  {product.productVariants
                    .sort((a, b) => {
                      const isValueA = a.agilityId === product.agilityId;
                      const isValueB = b.agilityId === product.agilityId;
                      return isValueA === isValueB ? 0 : isValueA ? -1 : 1;
                    })
                    .slice(0, product.productVariants.length > 5 ? 4 : 5)
                    .map((variant) => {
                      let isActive =
                        !!product.productVariants &&
                        product.productVariants.length > 0 &&
                        product.productVariants[0].agilityId === variant.agilityId;

                      if (activeVariantId !== undefined) {
                        isActive = activeVariantId === variant.agilityId;
                      }

                      return (
                        <s.VariantThumbnail
                          key={variant.agilityId}
                          aria-current={isActive}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setActiveVariantId(variant.agilityId);
                            setVariantImageUrl(variant.image);
                          }}
                        >
                          <img
                            alt={variant.name}
                            src={`${new ImageTransform(variant.image).width(60)}`}
                            loading="lazy"
                            draggable={false}
                          />
                        </s.VariantThumbnail>
                      );
                    })}
                  {product.productVariants.length > 5 && (
                    <s.OtherVariantsCount>+{product.productVariants.length - 4}</s.OtherVariantsCount>
                  )}
                </>
              ) : (
                // Empty element to preserve spacing/layout
                <s.VariantThumbnail aria-hidden="true" />
              )}
            </s.VariantSelector>
          )}

          <RatingStars
            averageReviewRating={product.averageReviewRating}
            numberOfRatings={product.numberOfRatings}
            reserveSpace={rowMapping?.hasReviews}
          />
          <s.Name data-longest-title={rowMapping?.longestTitle}>
            <span dangerouslySetInnerHTML={{ __html: product.title.replace('-/', '-/&ZeroWidthSpace;') }} />
          </s.Name>
          {showPrice && product.formattedProductPrice ? (
            <>
              {product.isOnSale && product.formattedProductPrice ? (
                <s.ProductStandardPrice>
                  {product.formattedProductPrice} <span>{translations['custom.milwaukee.promotion.emapping.vat']}</span>
                </s.ProductStandardPrice>
              ) : null}

              <s.ProductPrice>
                {product.formattedProductPrice} <span>{translations['custom.milwaukee.promotion.emapping.vat']}</span>
              </s.ProductPrice>

              <s.Disclaimer>{translations['custom.milwaukee.promotion.disclaimer']}</s.Disclaimer>
            </>
          ) : null}

          {product.isPublished || (!product.isPublished && !productInfoModal) ? (
            <Button
              text={buttonText}
              theme={getButtonTheme()}
              size="small"
              fakeButton={onClick ? false : true}
              style={{ visibility: displayAsLink ? undefined : 'hidden' }}
            />
          ) : (
            <>
              {rowMapping?.hasButton ? (
                <s.ButtonHider>
                  <Button
                    text={buttonText}
                    theme={getButtonTheme()}
                    size="small"
                    fakeButton={onClick ? false : true}
                    style={{ visibility: displayAsLink ? undefined : 'hidden' }}
                  />
                </s.ButtonHider>
              ) : null}
            </>
          )}

          {productInfoModal && translations['custom.product-info-modal.cta'] !== null && setShowInfoModal ? (
            <s.InfoModalCta
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (setShowInfoModal) {
                  setShowInfoModal(product);
                }
              }}
            >
              {translations['custom.product-info-modal.cta']} <Chevron />
            </s.InfoModalCta>
          ) : null}
        </s.CardContent>

        {product.includedProducts && product.includedProducts.length > 0 ? (
          <s.BundleDropdown isActive={dropdownActive} inView={animate ? inView : true} delay={delayInterval}>
            <s.BottomTitle
              onClick={(e) => {
                e.stopPropagation();
                setDropdownActive(!dropdownActive);
              }}
            >
              {translations['custom.milwaukee.promotion.whats.included']}
              <s.Icon>
                <Minus />
                <Minus />
              </s.Icon>
            </s.BottomTitle>
            <s.BottomContent>
              <ul>
                {product.includedProducts.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </s.BottomContent>
          </s.BundleDropdown>
        ) : null}
        {showCompare && (
          <s.CompareContainer
            inView={animate ? inView : true}
            delay={delayInterval}
            data-added={isInCompare}
            onClick={() => {
              if (updateCompare) {
                updateCompare(product.agilityId, product.pngListingImage, product.title, product.modelCode);
              }
            }}
          >
            {isInCompare || (compareItems && compareItems.length < 4) ? (
              <>
                <s.CompareBox>
                  <Tick />
                </s.CompareBox>
                <s.CompareText>
                  {isInCompare
                    ? translations['custom.milwaukee.compare.selected']
                    : translations['custom.milwaukee.compare']}
                </s.CompareText>
              </>
            ) : (
              <>
                <s.CompareText>{translations['custom.milwaukee.compare.max-products']}</s.CompareText>
              </>
            )}
          </s.CompareContainer>
        )}
      </s.Card>
    </>
  );
};

export function hasAnyIcons(product: ProductViewModel, mappings: ProductViewModelIconMapping[]): boolean {
  if (!product.icons) {
    return false;
  }

  return product.icons.find((x) => mappings.find((y) => x.iconGuid === y.iconMappingGuid) !== undefined) !== undefined;
}

export default ProductCard;
